import { CSSProperties } from "react"
import Job from "../models/Job"
import { withAlpha } from "../../../../core/Helper"

/**
 * Job-specific glue around the shared colour system (see components/color/ColorPalette.ts).
 *
 * A job has NO colour by default; a dispatcher paints one on to visually group jobs on the board.
 * It is deliberately a different visual language than the job STATUS colour (which stays a small
 * solid dot in the status column): a job colour is only ever rendered as a left edge band + a soft
 * tint that fades out, so the two can never be mistaken for each other.
 */

/** CSS custom properties consumed by the `.job-colored` rules in JobColor.css. */
export const jobColorVars = (color?: string): CSSProperties | undefined => {
    if (!color) return undefined
    return {
        ["--accent-color" as any]: color,
        ["--job-tint" as any]: withAlpha(color, .18),
        ["--job-tint-dark" as any]: withAlpha(color, .30),
        // Board row tints (Customize Board): fainter for "soft", the hover of a "full" row.
        ["--job-tint-soft" as any]: withAlpha(color, .11),
        ["--job-tint-soft-dark" as any]: withAlpha(color, .18),
        ["--job-tint-strong" as any]: withAlpha(color, .26),
        ["--job-tint-strong-dark" as any]: withAlpha(color, .40),
        ["--job-hover" as any]: withAlpha(color, .07),
        ["--job-hover-dark" as any]: withAlpha(color, .14),
    }
}

/** True when the current user is allowed to paint/clear this job's colour. */
export const canPaintJob = (job: Job, isTech?: boolean, canEditArchived?: boolean) =>
    !isTech && (!job.isArchived || !!canEditArchived)

/**
 * The colour the UI should paint for this job. It's a dispatcher-side organisation tool, so
 * technicians — who only ever see their own jobs — never get the accent.
 */
export const jobColorOf = (job?: Job, isTech?: boolean) => isTech ? undefined : job?.color
